/**
 * PNL Summary Card - мини-карточка для страницы профиля
 * Показывает краткую сводку PNL и кнопку "Поделиться"
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import SoundManager from '../services/SoundManager';
import './PnlSummaryCard.css';
import coinsIcon from '../assets/icons/coins.svg';

interface PnlSummaryProps {
  onOpenModal: () => void;
}

interface PnlSummary {
  totalEarnedSperm: number;
  totalEarnedUsdt: number;
  dailyIncomeSperm: number;
  dailyIncomeUsdt: number;
  activeReferrals: number;
  totalReferrals: number;
  availableLevel: number;
}

const PnlSummaryCard: React.FC<PnlSummaryProps> = ({ onOpenModal }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<PnlSummary | null>(null);

  useEffect(() => {
    loadSummary();
  }, []);

  const loadSummary = async () => {
    try {
      setLoading(true);
      const res = await api.get('/pnl/data');
      if (res.data.success) {
        setSummary(res.data.data);
      }
    } catch (err) {
      console.error('[PNL] Ошибка загрузки сводки:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number, decimals: number = 0): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString('ru-RU', { maximumFractionDigits: decimals });
  };

  const handleClick = () => {
    SoundManager.playModalOpen();
    onOpenModal();
  };

  return (
    <div className="pnl-summary-card" onClick={handleClick}>
      <div className="pnl-summary-header">
        <img src={coinsIcon} alt="" className="pnl-summary-icon-img" />
        <div className="pnl-summary-title">
          <span className="title-text">{t('pnl.myPnl')}</span>
          <span className="title-hint">{t('pnl.clickToShare')}</span>
        </div>
        <div className="pnl-summary-arrow">→</div>
      </div>

      {loading ? (
        <div className="pnl-summary-loading">
          <div className="pnl-mini-spinner"></div>
        </div>
      ) : summary ? (
        <div className="pnl-summary-stats">
          <div className="pnl-summary-stat">
            <span className="stat-icon">💰</span>
            <span className="stat-value positive">+{formatNumber(summary.totalEarnedSperm)}</span>
            <span className="stat-label">SPERM</span>
          </div>
          <div className="pnl-summary-divider"></div>
          <div className="pnl-summary-stat">
            <span className="stat-icon">💵</span>
            <span className="stat-value positive">+{formatNumber(summary.totalEarnedUsdt, 2)}</span>
            <span className="stat-label">USDT</span>
          </div>
          <div className="pnl-summary-divider"></div>
          <div className="pnl-summary-stat team-stat">
            <span className="stat-icon">👥</span>
            <span className="stat-value">{summary.totalReferrals}</span>
            <span className="stat-label">{t('pnl.team')}</span>
            <span className="stat-active">({t('pnl.active')}: {summary.activeReferrals})</span>
          </div>
        </div>
      ) : (
        <div className="pnl-summary-empty">
          <span>{t('pnl.noData')}</span>
        </div>
      )}
    </div>
  );
};

export default PnlSummaryCard;
